import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

const BidsReports = () => {
  const [allBids, setAllBids] = useState([]);
  const [successBids, setSuccessBids] = useState([]);
  const [expiredBids, setExpiredBids] = useState([]);
  const [filteredBids, setFilteredBids] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')); // mobile
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md')); // tablet

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [allRes, successRes, expiredRes] = await Promise.all([
          fetch('https://vyaparapi.emedha.in/api/admin/bids/all'),
          fetch('https://vyaparapi.emedha.in/api/admin/bids/success'),
          fetch('https://vyaparapi.emedha.in/api/admin/bids/expired'),
        ]);

        const [allData, successData, expiredData] = await Promise.all([
          allRes.json(),
          successRes.json(),
          expiredRes.json(),
        ]);

        setAllBids(allData);
        setSuccessBids(successData);
        setExpiredBids(expiredData);
        setFilteredBids(allData);
      } catch (err) {
        console.error('Error fetching bids:', err);
      }
    };

    fetchAll();
  }, []);

  useEffect(() => {
    if (statusFilter === 'success') setFilteredBids(successBids);
    else if (statusFilter === 'expired') setFilteredBids(expiredBids);
    else setFilteredBids(allBids);
  }, [statusFilter, allBids, successBids, expiredBids]);

  const displayedRows = filteredBids.filter(bid => {
    const bidId = String(bid.bid_id || '').toLowerCase();
    const subCat = String(bid.sub_category || '').toLowerCase();
    const keyword = search.toLowerCase();
    return bidId.includes(keyword) || subCat.includes(keyword);
  });

  const columns = [
    { field: 'bid_id', headerName: 'Bid ID', width: 210 },
    { field: 'sub_category', headerName: 'Product Name', width: 110 },
    { field: 'price_from', headerName: 'Price From', width: 110 },
    { field: 'price_to', headerName: 'Price To', width: 110 },
    { field: 'bid_created_at', headerName: 'Created At', width: 110 },
    { field: 'seller_bid_created_at', headerName: 'Seller Bid Accepted', width: 160 },
    { field: 'seller_bid_accepted_at', headerName: 'Accepted', width: 110 },
    { field: 'seller_bid_status', headerName: 'Status', width: 110 },
  ];

  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(displayedRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bids');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const file = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(file, 'bids_reports.xlsx');
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h5" gutterBottom>
        Bids Reports
      </Typography>

      {/* Responsive Controls */}
      <Box
        sx={{
         display: 'flex', gap: 2, mb: 2 
        }}
      >
        <TextField
          label="Search by Bid ID / Product"
          variant="outlined"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth={isSmallScreen || isMediumScreen}
    
        />
        <FormControl fullWidth={isSmallScreen || isMediumScreen} sx={{ minWidth: 100 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status Filter"
          >
            <MenuItem value="all">All Bids</MenuItem>
            <MenuItem value="success">Success Bids</MenuItem>
            <MenuItem value="expired">Expired Bids</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={handleExport}
          fullWidth={isSmallScreen || isMediumScreen}
          sx={{ height: 56 }}
        >
          Export to Excel
        </Button>
      </Box>

      {/* Responsive Table Container */}
      <Box sx={{ height: 500, width: '100%'}}>
        <DataGrid
          rows={displayedRows.map((row, index) => ({ id: index + 1, ...row }))}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[7, 15, 30]}
          disableSelectionOnClick
          autoHeight={false}
        />
      </Box>
    </Box>
  );
};

export default BidsReports;
